import type { Announcement, Message } from "../../services/communicationService";
import { byText, reversed, type RegisterConfig } from "../register/registerLogic";

/** Views, filters and sorts for the Communication page's two lists. */

// Messages ---------------------------------------------------------------------------

/** Where a message stands for the person looking at it. */
export function messageStanding(message: Message, userId: number | undefined) {
  if (message.sender === userId && message.recipient !== userId) {
    return message.read_at
      ? { key: "sent", label: "Sent · read", dot: "bg-sky-500" }
      : { key: "sent", label: "Sent", dot: "bg-gray-400" };
  }
  return message.read_at
    ? { key: "read", label: "Read", dot: "bg-gray-300" }
    : { key: "unread", label: "Unread", dot: "bg-primary-600" };
}

export const isIncoming = (message: Message, userId: number | undefined) => message.recipient === userId;

/** The other side of the conversation: who wrote it, or who it went to. */
export const otherParty = (message: Message, userId: number | undefined) =>
  isIncoming(message, userId)
    ? { id: message.sender, name: message.sender_name || "Unknown sender" }
    : { id: message.recipient, name: message.recipient_name || "Unknown recipient" };

/** "Re: Fees" for a reply, without piling up "Re: Re:". */
export const replySubject = (subject: string) => (/^re:/i.test(subject.trim()) ? subject.trim() : `Re: ${subject.trim()}`);

export const messageConfig = (userId: number | undefined): RegisterConfig<Message> => ({
  search: (message) => [message.subject, message.content, message.sender_name, message.recipient_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "inbox", label: "Inbox", match: (message) => isIncoming(message, userId) },
    { key: "unread", label: "Unread", match: (message) => isIncoming(message, userId) && !message.read_at },
    { key: "sent", label: "Sent", match: (message) => message.sender === userId },
  ],
  date: { label: "Sent", value: (message) => message.created_at },
  sorts: [
    { key: "-at", label: "Newest first", compare: reversed(byText<Message>((message) => message.created_at)) },
    { key: "at", label: "Oldest first", compare: byText<Message>((message) => message.created_at) },
    { key: "person", label: "Person (A–Z)", compare: byText<Message>((message) => otherParty(message, userId).name) },
    { key: "subject", label: "Subject (A–Z)", compare: byText<Message>((message) => message.subject) },
  ],
});

// Announcements ----------------------------------------------------------------------

export const AUDIENCES: Record<Announcement["target_audience"], string> = {
  all: "Everyone",
  parents: "Parents",
  teachers: "Teachers",
  students: "Students",
};

export const PRIORITIES: Record<Announcement["priority"], { label: string; dot: string; rank: number }> = {
  urgent: { label: "Urgent", dot: "bg-red-500", rank: 0 },
  high: { label: "High", dot: "bg-amber-500", rank: 1 },
  normal: { label: "Normal", dot: "bg-gray-400", rank: 2 },
  low: { label: "Low", dot: "bg-gray-300", rank: 3 },
};

export const ANNOUNCEMENT_TYPES: Record<Announcement["announcement_type"], string> = {
  general: "General",
  academic: "Academic",
  financial: "Financial",
  event: "Event",
  emergency: "Emergency",
};

export const priorityOf = (announcement: Announcement) => PRIORITIES[announcement.priority] ?? PRIORITIES.normal;
export const publishedOn = (announcement: Announcement) => announcement.published_at || announcement.created_at;

/** Past its "shown until" day; `today` is yyyy-mm-dd. */
export const hasExpired = (announcement: Announcement, today: string) =>
  !!announcement.expires_at && announcement.expires_at.slice(0, 10) < today;

export const announcementConfig = (today: string): RegisterConfig<Announcement> => ({
  search: (item) => [item.title, item.content, item.author_name].join(" "),
  views: [
    { key: "current", label: "Current", match: (item) => !hasExpired(item, today) },
    { key: "important", label: "Urgent & high", match: (item) => !hasExpired(item, today) && priorityOf(item).rank <= 1 },
    { key: "expired", label: "Expired", match: (item) => hasExpired(item, today) },
    { key: "all", label: "All" },
  ],
  date: { label: "Published", value: publishedOn },
  category: { label: "Audience", value: (item) => AUDIENCES[item.target_audience] ?? item.target_audience },
  sorts: [
    { key: "-at", label: "Newest first", compare: reversed(byText<Announcement>(publishedOn)) },
    { key: "priority", label: "Most urgent first", compare: (a, b) => priorityOf(a).rank - priorityOf(b).rank || publishedOn(b).localeCompare(publishedOn(a)) },
    { key: "at", label: "Oldest first", compare: byText<Announcement>(publishedOn) },
    { key: "title", label: "Title (A–Z)", compare: byText<Announcement>((item) => item.title) },
  ],
});
